/**
 * TINY THING #001: Flow Executor
 *
 * Problem Solved: Stale state in step sequences
 * Verified: Always gets fresh state, can't go stale
 * Usage: Pass a getter function instead of static array
 *
 * @verified 2025-09-24
 */

export interface FlowStep<T = any> {
  id: string;
  name: string;
  action: () => Promise<FlowResult<T>>;
}

export interface FlowResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export interface FlowState {
  currentStep: number;
  steps: FlowStep[];
  results: Map<string, FlowResult>;
  isRunning: boolean;
  completedAt?: number;
}

export class VerifiedFlowExecutor {
  private state: FlowState = {
    currentStep: -1,
    steps: [],
    results: new Map(),
    isRunning: false
  };

  private listeners: Map<string, Set<Function>> = new Map();

  /**
   * Execute flow with guaranteed fresh state
   * @param stepGetter Function that returns current steps (prevents stale state)
   */
  async execute(stepGetter: () => FlowStep[]): Promise<FlowResult[]> {
    this.state.isRunning = true;
    this.state.results.clear();
    this.emit('flow:start');

    const results: FlowResult[] = [];

    try {
      // Get fresh steps each iteration - PREVENTS STALE STATE
      let stepIndex = 0;
      let shouldContinue = true;

      while (shouldContinue) {
        const currentSteps = stepGetter(); // ALWAYS FRESH

        if (stepIndex >= currentSteps.length) break;

        const step = currentSteps[stepIndex];
        this.state.currentStep = stepIndex;

        this.emit('step:start', { step: step.name, index: stepIndex });

        try {
          const result = await step.action();
          this.state.results.set(step.id, result);
          results.push(result);

          this.emit('step:complete', {
            step: step.name,
            success: result.success,
            data: result.data
          });

          if (!result.success) {
            shouldContinue = false;
            this.emit('flow:error', {
              step: step.name,
              error: result.error
            });
          }

          stepIndex++;
        } catch (error) {
          const errorResult: FlowResult = {
            success: false,
            error: error instanceof Error ? error.message : String(error),
            timestamp: Date.now()
          };

          this.state.results.set(step.id, errorResult);
          results.push(errorResult);
          shouldContinue = false;

          this.emit('flow:error', {
            step: step.name,
            error: errorResult.error
          });
        }
      }

      this.state.completedAt = Date.now();
      this.emit('flow:complete', { results });

    } finally {
      this.state.isRunning = false;
    }

    return results;
  }

  /**
   * Subscribe to flow events
   */
  on(event: string, handler: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  /**
   * Emit event to listeners
   */
  private emit(event: string, data?: any): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  /**
   * Get current execution state
   */
  getState(): FlowState {
    return { ...this.state };
  }

  /**
   * Reset executor for reuse
   */
  reset(): void {
    this.state = {
      currentStep: -1,
      steps: [],
      results: new Map(),
      isRunning: false
    };
    this.emit('flow:reset');
  }
}

/**
 * React Hook Version
 */
import { useState, useCallback, useRef } from 'react';

export function useFlowExecutor() {
  const [state, setState] = useState<FlowState>({
    currentStep: -1,
    steps: [],
    results: new Map(),
    isRunning: false
  });

  const executorRef = useRef(new VerifiedFlowExecutor());

  const execute = useCallback(async (stepGetter: () => FlowStep[]) => {
    setState(prev => ({ ...prev, isRunning: true }));

    const executor = executorRef.current;

    executor.on('step:complete', (data) => {
      setState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1
      }));
    });

    const results = await executor.execute(stepGetter);

    setState(prev => ({
      ...prev,
      isRunning: false,
      completedAt: Date.now()
    }));

    return results;
  }, []);

  const reset = useCallback(() => {
    executorRef.current.reset();
    setState({
      currentStep: -1,
      steps: [],
      results: new Map(),
      isRunning: false
    });
  }, []);

  return {
    state,
    execute,
    reset,
    isRunning: state.isRunning,
    currentStep: state.currentStep
  };
}